import { Button, Tag, Text } from '@wfp/react';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import TableMain from 'src/components/TableMain';
import { API_HOST, CURRENT_STATE_LABELS, STATUS_COLORS } from 'src/constants';
import type { PublicationResultColumn } from 'src/crf';
import { resolveCrfByPeriod } from 'src/crf';
import { receiveAPIResponseHoldingData } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { REPORT_LIST_DATA_KEY } from 'src/redux/constants';
import { openACRFile, showNotification } from 'src/utils';
import OperationIdCell from '../OperationIdCell';
import ACRFileCell from './ACRFileCell';

interface Props {
  data?: any[];
  filterText: (column: any) => React.ReactNode;
  filterSelect: (
    accessor: string,
    column: any,
    exact?: boolean,
  ) => React.ReactNode;
  period?: string | number;
}

const ProjectCodeCell = ({ row }: { row: any }) => (
  <OperationIdCell row={row} />
);

const AcrFileCellRenderer = ({ value, row, column }: any) => (
  <ACRFileCell
    type={column.acrType}
    value={value}
    rowValues={row.original}
    period={column.period}
  />
);

const CurrentStatusCell = ({ value }: { value: any }) => {
  const statusLabel = CURRENT_STATE_LABELS[value] || value;
  const colors = STATUS_COLORS[statusLabel] || {
    background: '#E0E0E0',
    color: '#333333',
  };
  return (
    <Tag
      style={{
        backgroundColor: colors.background,
        color: colors.color,
      }}
    >
      {statusLabel}
    </Tag>
  );
};

const MergeStatusCell = ({ value }: { value: any }) => (
  <div className="publication-merge-status">{value || '-'}</div>
);

const ReportSubmittedCell = ({ row, column }: { row: any; column: any }) => {
  const { reportSubmitting, submitReport } = column;
  const { link, preview_link, pk } = row.original;
  if (link) return <Text>Yes</Text>;
  if (preview_link) {
    return (
      <Button
        disabled={reportSubmitting === pk}
        onClick={() => submitReport(pk)}
        data-test-id="submit-button"
      >
        Submit
      </Button>
    );
  }

  return <Text>No</Text>;
};

const ReportLinkCell = ({
  row: {
    original: { link, preview_link },
  },
}: any) => {
  if (link)
    return (
      <a
        className="acr-file-link file-submitted"
        href={link}
        target="_blank"
        rel="noreferrer"
        data-test-id="show-submitted-acr-link"
      >
        Show submitted ACR
      </a>
    );
  if (preview_link)
    return (
      <div
        className="acr-file-link"
        onClick={() => openACRFile(`https://${preview_link}`)}
        data-test-id="show-acr-preview-link"
      >
        Show ACR preview
      </div>
    );
  return '';
};

const PublicationLinkCell = ({ value, column }: any) => {
  if (!value) return '';

  const href = `https://${value}`;

  if (column.kind === 'restrictedLink')
    return (
      <div
        className="acr-file-link"
        onClick={() => openACRFile(href)}
        data-test-id="show-restricted-link"
      >
        Show restricted ACR
      </div>
    );

  return (
    <a
      className="acr-file-link file-submitted"
      href={href}
      target="_blank"
      rel="noreferrer"
      data-test-id="show-public-link"
    >
      Show public ACR
    </a>
  );
};

const PublicationStatusTab = ({
  data,
  filterText,
  filterSelect,
  period,
}: Props) => {
  const dispatch = useDispatch();
  const [reportSubmitting, setReportSubmitting] = useState<
    string | number | null
  >(null);

  const crf = resolveCrfByPeriod(period);

  const processedData = useMemo(() => {
    if (!data) return [];

    return data.map((reportRow: any) => ({
      ...reportRow,
      merge_status: crf.calcStatus(reportRow),
    }));
  }, [data, crf]);

  async function submitReport(pk: string | number) {
    setReportSubmitting(pk);
    const url = `${API_HOST}/api/acr-merge/submit/${pk}/`;

    try {
      const response = await apiCallAsync(url, null, 'post');

      dispatch(
        receiveAPIResponseHoldingData(response.data, REPORT_LIST_DATA_KEY),
      );
      showNotification('Report submitted successfully', 'success');
      setReportSubmitting(null);
    } catch (err) {
      console.error(err);

      showNotification('Error while submitting the report', 'error');
      setReportSubmitting(null);
    }
  }

  function buildResultColumn(resultColumn: PublicationResultColumn) {
    const { kind, header, accessor } = resultColumn;

    if (kind === 'submitted')
      return {
        Header: header,
        accessor: ({ link, preview_link }) => {
          if (link) return 'Yes';
          if (preview_link) return 'Submit';
          return 'No';
        },
        id: 'report_submitted_column',
        reportSubmitting,
        submitReport,
        Filter: () => null,
        Cell: ReportSubmittedCell,
      };

    if (kind === 'reportLink')
      return {
        Header: header,
        accessor: ({ link, preview_link }) => {
          if (link) return 'Show submitted ACR';
          if (preview_link) return 'Show ACR preview';
          return '';
        },
        id: 'report_link_column',
        Filter: () => null,
        Cell: ReportLinkCell,
      };

    return {
      Header: header,
      accessor,
      id: `${accessor}_column`,
      kind,
      Filter: () => null,
      Cell: PublicationLinkCell,
    };
  }

  const columns = [
    {
      Header: 'Operation ID',
      accessor: 'project_code',
      id: 'project_code_column',
      Filter: column => filterText(column),
      Cell: ProjectCodeCell,
    },
    ...crf.fileColumns.map(({ type, header, accessor }) => ({
      Header: header,
      accessor,
      id: `${accessor}_column`,
      acrType: type,
      period,
      Filter: () => null,
      Cell: AcrFileCellRenderer,
    })),
    {
      Header: 'Project Status',
      accessor: 'current_status',
      id: 'current_status_column',
      className: 'current-status-column',
      Cell: CurrentStatusCell,
      Filter: column => filterSelect('current_status', column),
    },
    {
      Header: crf.statusColumnHeader,
      accessor: 'merge_status',
      id: 'merge_status_column',
      filter: 'equals',
      Filter: column => filterSelect('merge_status', column, true),
      Cell: MergeStatusCell,
    },
    ...crf.resultColumns.map(buildResultColumn),
  ];

  return (
    <TableMain
      data={processedData}
      loading={!processedData || processedData.length === 0}
      columns={columns}
      pagination="client"
      className="-striped -highlight"
      isFilterable
      initialState={{
        sortBy: [
          {
            id: 'project_code_column',
            desc: false,
          },
        ],
      }}
    />
  );
};

export default PublicationStatusTab;
